import React, { useCallback } from 'react';
import { Sparkles, Send, Lightbulb } from 'lucide-react';
import { useReportBuilderState } from '../hooks/useReportBuilderState';
import { AI_EXAMPLE_PROMPTS } from '../constants';

interface AIReportPanelProps {
  isLoading: boolean;
  explanation: string;
  hasDataSources: boolean;
  onGenerate: (prompt: string) => void;
}

const AIReportPanel: React.FC<AIReportPanelProps> = ({
  isLoading,
  explanation,
  hasDataSources,
  onGenerate,
}) => {
  const aiPrompt = useReportBuilderState(s => s.aiPrompt);
  const setAiPrompt = useReportBuilderState(s => s.setAiPrompt);

  const handleSubmit = useCallback(() => {
    if (aiPrompt.trim()) onGenerate(aiPrompt.trim());
  }, [aiPrompt, onGenerate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  return (
    <div className="crb-ai-panel">
      <div className="crb-ai-header">
        <div className="crb-ai-badge">
          <Sparkles size={16} />
          AI Report Assistant
        </div>
        <p className="crb-ai-subtitle">Describe the report you need in plain English</p>
      </div>

      <div className="crb-ai-input-row">
        <textarea
          className="crb-ai-textarea"
          placeholder="e.g., Show me all users grouped by department with completion rates..."
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={isLoading}
        />
        <button
          className="crb-ai-send-btn"
          onClick={handleSubmit}
          disabled={isLoading || !aiPrompt.trim() || !hasDataSources}
        >
          {isLoading ? (
            <div className="crb-spinner" style={{ width: 18, height: 18, borderWidth: 2, marginBottom: 0 }} />
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>

      <div className="crb-ai-examples">
        {AI_EXAMPLE_PROMPTS.map((prompt, i) => (
          <button
            key={i}
            className="crb-ai-example-chip"
            onClick={() => setAiPrompt(prompt)}
            disabled={isLoading}
          >
            {prompt}
          </button>
        ))}
      </div>

      {explanation && (
        <div className="crb-ai-explanation">
          <Lightbulb size={14} />
          <span>{explanation}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(AIReportPanel);
